// ./src/utils/generateSubmissionPdf.ts
import dayjs from "dayjs";

import { Submission, SubmissionFile, Form, FormField } from "@/utils/api";
import { FIELD_TYPE_IDS } from "@/config/fieldTypes";

export interface GenerateSubmissionPdfOptions {
  /** Submission data */
  submission: Submission;
  /** Form configuration */
  form: Form;
  /** Form field definitions */
  formFields: FormField[];
  /** Whether to include metadata (IP, user agent, referrer) */
  includeMetadata?: boolean;
  /** Whether to show full credit card numbers */
  showFullCreditCard?: boolean;
  /** Whether to include logo/branding */
  showLogo?: boolean;
}

// PDF Constants
const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm
const MARGIN_LEFT = 20;
const MARGIN_RIGHT = 20;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 20;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
const LINE_HEIGHT = 6;
const SECTION_GAP = 10;

// Font sizes
const FONT_SIZE_TITLE = 16;
const FONT_SIZE_SECTION_HEADER = 11;
const FONT_SIZE_LABEL = 9;
const FONT_SIZE_VALUE = 10;
const FONT_SIZE_FOOTER = 8;

/**
 * Masks a credit card number showing only last 4 digits
 */
const maskCreditCard = (value: string): string => {
  if (!value || typeof value !== "string") return "";
  const cleaned = value.replace(/\s|-/g, "");
  if (cleaned.length < 4) return "••••";
  const lastFour = cleaned.slice(-4);
  const maskedPortion = "•".repeat(cleaned.length - 4);
  return `${maskedPortion}${lastFour}`;
};

/**
 * Formats a credit card number with spaces
 */
const formatCreditCard = (value: string): string => {
  if (!value || typeof value !== "string") return "";
  const cleaned = value.replace(/\s|-/g, "");
  return cleaned.replace(/(.{4})/g, "$1 ").trim();
};

/**
 * Formats a value based on field type
 */
const formatFieldValue = (
  field: FormField,
  value: any,
  showFullCreditCard: boolean
): string => {
  if (value === null || value === undefined || value === "") {
    return "No value provided";
  }

  const fieldType = field.fieldType;

  switch (fieldType) {
    case FIELD_TYPE_IDS.DATE:
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY");
      }
      return String(value);

    case FIELD_TYPE_IDS.TIME:
      if (value && typeof value === "string") {
        const timeParts = value.split(":");
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0], 10);
          const minutes = timeParts[1];
          const period = hours >= 12 ? "PM" : "AM";
          const displayHours = hours % 12 || 12;
          return `${displayHours}:${minutes} ${period}`;
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.DATETIME:
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY [at] h:mm A");
      }
      return String(value);

    case FIELD_TYPE_IDS.BIRTHDATE:
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY");
      }
      return String(value);

    case FIELD_TYPE_IDS.BIRTHDAY:
      if (value && typeof value === "string") {
        const parts = value.split("-");
        if (parts.length === 2) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          if (month >= 1 && month <= 12) {
            return `${monthNames[month - 1]} ${day}`;
          }
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.BOOLEAN:
      if (
        value === true ||
        value === "true" ||
        value === "yes" ||
        value === "1"
      ) {
        return "Yes";
      }
      if (
        value === false ||
        value === "false" ||
        value === "no" ||
        value === "0"
      ) {
        return "No";
      }
      return String(value);

    case FIELD_TYPE_IDS.CREDIT_CARD:
      if (showFullCreditCard) {
        return formatCreditCard(String(value));
      }
      return maskCreditCard(String(value));

    case FIELD_TYPE_IDS.NUMBER:
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num.toLocaleString();
      }
      return String(value);

    case FIELD_TYPE_IDS.MULTIPLE_CHOICE:
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "string" && value.startsWith("[")) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.join(", ");
          }
        } catch {
          // Fall through
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.COORDINATES:
      if (typeof value === "string") {
        return value;
      }
      if (
        typeof value === "object" &&
        value.lat !== undefined &&
        value.lng !== undefined
      ) {
        return `${value.lat}, ${value.lng}`;
      }
      return String(value);

    default:
      return String(value);
  }
};

/**
 * Formats file size in human-readable format
 */
const formatFileSize = (bytes: number | string | undefined): string => {
  if (bytes === undefined || bytes === null) return "";

  const numBytes = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;

  if (isNaN(numBytes) || numBytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Gets printable fields (excludes files, anti-spam)
 */
const getPrintableFields = (formFields: FormField[]): FormField[] => {
  const skipTypes = [
    FIELD_TYPE_IDS.FILE,
    FIELD_TYPE_IDS.ANTISPAMBOT,
    FIELD_TYPE_IDS.RECAPTCHA2,
    FIELD_TYPE_IDS.RECAPTCHA3,
    FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE,
  ];

  return formFields.filter(
    (field) =>
      field.fieldType === undefined || !skipTypes.includes(field.fieldType)
  );
};

/**
 * Parses formData JSON string
 */
const parseFormData = (formData: string | undefined): Record<string, any> => {
  if (!formData) return {};

  try {
    return JSON.parse(formData);
  } catch {
    return {};
  }
};

/**
 * Gets a file type label from MIME type
 */
const getFileTypeLabel = (mimeType: string | undefined): string => {
  if (!mimeType) return "File";

  const mimeMap: Record<string, string> = {
    "application/pdf": "PDF",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word",
    "application/vnd.ms-excel": "Excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel",
    "image/jpeg": "Image",
    "image/png": "Image",
    "image/gif": "Image",
    "text/plain": "Text",
    "text/csv": "CSV",
  };

  if (mimeType.startsWith("image/")) return "Image";
  if (mimeType.startsWith("video/")) return "Video";
  if (mimeType.startsWith("audio/")) return "Audio";

  return mimeMap[mimeType] || "File";
};

/**
 * Generate a PDF for a submission
 * Uses dynamic import to load jsPDF only when needed
 */
export const generateSubmissionPdf = async (
  options: GenerateSubmissionPdfOptions
): Promise<void> => {
  // Dynamic import - jsPDF is only loaded when this function is called
  const { jsPDF } = await import("jspdf");

  /**
   * PDF Builder class to manage document creation
   */
  class PdfBuilder {
    private doc: InstanceType<typeof jsPDF>;
    private currentY: number;
    private pageNumber: number;

    constructor() {
      this.doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      this.currentY = MARGIN_TOP;
      this.pageNumber = 1;
    }

    /**
     * Check if we need a new page
     */
    private checkPageBreak(requiredHeight: number): void {
      if (this.currentY + requiredHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
        this.doc.addPage();
        this.pageNumber++;
        this.currentY = MARGIN_TOP;
      }
    }

    /**
     * Add the document title
     */
    addTitle(text: string): void {
      this.checkPageBreak(15);
      this.doc.setFontSize(FONT_SIZE_TITLE);
      this.doc.setFont("helvetica", "bold");
      this.doc.text(text, MARGIN_LEFT, this.currentY);
      this.currentY += LINE_HEIGHT * 1.5;
    }

    /**
     * Add subtitle/description
     */
    addSubtitle(text: string): void {
      this.checkPageBreak(10);
      this.doc.setFontSize(FONT_SIZE_VALUE);
      this.doc.setFont("helvetica", "normal");
      this.doc.setTextColor(100, 100, 100);

      const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH);
      this.doc.text(lines, MARGIN_LEFT, this.currentY);
      this.currentY += lines.length * LINE_HEIGHT;

      this.doc.setTextColor(0, 0, 0);
    }

    /**
     * Add metadata row (label: value on same line)
     */
    addMetaRow(label: string, value: string): void {
      this.checkPageBreak(8);
      this.doc.setFontSize(FONT_SIZE_LABEL);
      this.doc.setFont("helvetica", "normal");
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(label + ":", MARGIN_LEFT, this.currentY);

      this.doc.setFontSize(FONT_SIZE_VALUE);
      this.doc.setFont("helvetica", "normal");
      this.doc.setTextColor(0, 0, 0);
      this.doc.text(value, MARGIN_LEFT + 35, this.currentY);

      this.currentY += LINE_HEIGHT;
    }

    /**
     * Add a section header
     */
    addSectionHeader(text: string): void {
      this.checkPageBreak(15);
      this.currentY += SECTION_GAP / 2;

      this.doc.setFontSize(FONT_SIZE_SECTION_HEADER);
      this.doc.setFont("helvetica", "bold");
      this.doc.text(text.toUpperCase(), MARGIN_LEFT, this.currentY);
      this.currentY += 2;

      // Draw underline
      this.doc.setDrawColor(150, 150, 150);
      this.doc.setLineWidth(0.5);
      this.doc.line(
        MARGIN_LEFT,
        this.currentY,
        PAGE_WIDTH - MARGIN_RIGHT,
        this.currentY
      );

      this.currentY += SECTION_GAP / 2;
    }

    /**
     * Add a field (label + value)
     */
    addField(label: string, value: string, isEmpty: boolean = false): void {
      // Calculate required height
      const valueLines = this.doc.splitTextToSize(value, CONTENT_WIDTH);
      const requiredHeight = LINE_HEIGHT + valueLines.length * LINE_HEIGHT + 4;

      this.checkPageBreak(requiredHeight);

      // Label
      this.doc.setFontSize(FONT_SIZE_LABEL);
      this.doc.setFont("helvetica", "bold");
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(label.toUpperCase(), MARGIN_LEFT, this.currentY);
      this.currentY += LINE_HEIGHT;

      // Value
      this.doc.setFontSize(FONT_SIZE_VALUE);
      this.doc.setFont("helvetica", isEmpty ? "italic" : "normal");
      this.doc.setTextColor(
        isEmpty ? 150 : 0,
        isEmpty ? 150 : 0,
        isEmpty ? 150 : 0
      );

      this.doc.text(valueLines, MARGIN_LEFT, this.currentY);
      this.currentY += valueLines.length * LINE_HEIGHT;

      // Light separator line
      this.doc.setDrawColor(220, 220, 220);
      this.doc.setLineWidth(0.2);
      this.doc.line(
        MARGIN_LEFT,
        this.currentY + 1,
        PAGE_WIDTH - MARGIN_RIGHT,
        this.currentY + 1
      );
      this.currentY += 5;

      // Reset colors
      this.doc.setTextColor(0, 0, 0);
    }

    /**
     * Add a file entry
     */
    addFileEntry(fileName: string, fileSize: string, fileType: string): void {
      this.checkPageBreak(10);

      this.doc.setFontSize(FONT_SIZE_VALUE);
      this.doc.setFont("helvetica", "normal");
      this.doc.text(`• ${fileName}`, MARGIN_LEFT + 2, this.currentY);

      this.doc.setFontSize(FONT_SIZE_LABEL);
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(
        `  (${fileType}, ${fileSize})`,
        MARGIN_LEFT + 4 + this.doc.getTextWidth(`• ${fileName}`),
        this.currentY
      );

      this.doc.setTextColor(0, 0, 0);
      this.currentY += LINE_HEIGHT;
    }

    /**
     * Add vertical space
     */
    addSpace(height: number): void {
      this.currentY += height;
    }

    /**
     * Add footer to all pages
     */
    addFooter(showBranding: boolean): void {
      const totalPages = this.doc.getNumberOfPages();
      const generatedAt = dayjs().format("MMMM D, YYYY [at] h:mm A");

      for (let i = 1; i <= totalPages; i++) {
        this.doc.setPage(i);
        this.doc.setFontSize(FONT_SIZE_FOOTER);
        this.doc.setFont("helvetica", "normal");
        this.doc.setTextColor(150, 150, 150);

        // Left side: generation date
        let footerText = `Generated on ${generatedAt}`;
        if (showBranding) {
          footerText += " • FormSubmits.com";
        }
        this.doc.text(footerText, MARGIN_LEFT, PAGE_HEIGHT - 10);

        // Right side: page number
        const pageText = `Page ${i} of ${totalPages}`;
        const pageTextWidth = this.doc.getTextWidth(pageText);
        this.doc.text(
          pageText,
          PAGE_WIDTH - MARGIN_RIGHT - pageTextWidth,
          PAGE_HEIGHT - 10
        );
      }
    }

    /**
     * Save the PDF with the given filename
     */
    save(filename: string): void {
      this.doc.save(filename);
    }

    /**
     * Get the PDF as a Blob
     */
    getBlob(): Blob {
      return this.doc.output("blob");
    }
  }

  // Extract options
  const {
    submission,
    form,
    formFields,
    includeMetadata = false,
    showFullCreditCard = false,
    showLogo = true,
  } = options;

  const pdf = new PdfBuilder();
  const formData = parseFormData(submission.formData);
  const printableFields = getPrintableFields(formFields);
  const files: SubmissionFile[] = submission.submissionFiles || [];

  // Title
  pdf.addTitle(form.name || "Form Submission");

  // Description
  if (form.description) {
    pdf.addSubtitle(form.description);
  }

  pdf.addSpace(4);

  // Submission info
  const submissionDate = submission.submissionDttm
    ? dayjs(submission.submissionDttm).format("MMMM D, YYYY [at] h:mm A")
    : "Unknown";
  pdf.addMetaRow("Submission Date", submissionDate);
  pdf.addMetaRow("Submission ID", submission.id || "—");

  // Metadata section (optional)
  if (includeMetadata) {
    pdf.addSectionHeader("Submission Metadata");
    pdf.addField(
      "IP Address",
      submission.remoteIp || "Unknown",
      !submission.remoteIp
    );
    pdf.addField(
      "User Agent",
      submission.userAgent || "Unknown",
      !submission.userAgent
    );
    pdf.addField(
      "Referrer",
      submission.referrer || "Direct / No referrer",
      !submission.referrer
    );
  }

  // Form fields section
  pdf.addSectionHeader("Submitted Data");

  if (printableFields.length > 0) {
    printableFields.forEach((field) => {
      const label = field.label || field.name || "Unnamed Field";
      const rawValue = formData[field.name || ""];
      const formattedValue = formatFieldValue(
        field,
        rawValue,
        showFullCreditCard
      );
      const isEmpty =
        rawValue === null || rawValue === undefined || rawValue === "";

      pdf.addField(label, formattedValue, isEmpty);
    });
  } else {
    pdf.addField("", "No form fields configured", true);
  }

  // Files section
  if (files.length > 0) {
    pdf.addSectionHeader(`Uploaded Files (${files.length})`);

    files.forEach((file) => {
      const fileName = file.fileName || "Unnamed file";
      const fileSize = formatFileSize(file.fileSize);
      const fileType = getFileTypeLabel(file.mimeType);

      pdf.addFileEntry(fileName, fileSize, fileType);
    });
  }

  // Add footer to all pages
  pdf.addFooter(showLogo);

  // Generate filename and save
  const timestamp = dayjs().format("YYYY-MM-DD");
  const formName = (form.name || "submission")
    .replace(/[^a-zA-Z0-9]/g, "-")
    .toLowerCase();
  const filename = `${formName}-${
    submission.id?.slice(0, 8) || "unknown"
  }-${timestamp}.pdf`;

  pdf.save(filename);
};
